import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE_URL } from "../config/apiconfig";

import { 
  ChevronLeft, 
  ChevronRight, 
  CheckCircle2, 
  Upload,
  X,
  User
} from "lucide-react";

const INDUSTRIES = [
  "IT Sector",
  "Garment Sector",
  "Construction",
  "Hospitality",
  "Manufacturing",
  "Logistics",
  "Retail",
];

const GARMENT = {
  departments: [
    "Cutting",
    "Sewing",
    "Knitting",
    "Finishing",
    "Quality Assurance",
    "Embroidery",
    "Washing",
  ],
  positionsByDepartment: {
    Cutting: ["Cutter", "Marker Maker", "Cutting Helper"],
    Sewing: ["Sewing Operator", "Line Leader", "Mechanic", "Sewing Helper"],
    Knitting: ["Knitting Operator", "Technician"],
    Finishing: ["Finishing Operator", "Finishing Helper"],
    "Quality Assurance": ["QA Inspector", "QA Supervisor"],
    Embroidery: ["Embroidery Operator", "Embroidery Helper"],
    Washing: ["Washing Operator", "Laundry Assistant"],
  },
  machines: [
    "Overlock Machine",
    "Lockstitch Sewing Machine",
    "Coverstitch Machine",
    "Bartack Machine",
    "Buttonhole Machine",
    "Button Attach Machine",
    "Flatlock Machine",
  ],
  products: ["Bras", "Panties", "T-Shirts", "Denims", "Shirts", "Sportswear"],
};

export default function Onboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const redirectedRef = useRef(false);

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    location: "",
    phoneCode: "+94",
    phoneNumber: "",
    email: "",
    industry: "",
    department: "",
    position: "",
    machines: [],
    products: [],
    onboarding_step: 0,
    onboarding_completed: 0,
  });
  // New state driven by backend lookup
  const [productOptions, setProductOptions] = useState([]); // [{id, product_code, name}]
  // Entries model: array of { productId, machineOptions, machineExperience(map) }
  const [entries, setEntries] = useState([
    { productId: null, machineOptions: [], machineExperience: {} }
  ]);
  const [profilePicture, setProfilePicture] = useState(null);
  // Country calling codes
  const [countryOptions, setCountryOptions] = useState([]); // [{ value: '+94', label: '🇱🇰 +94 Sri Lanka', cc: 'LK', country: 'Sri Lanka' }]
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [countriesError, setCountriesError] = useState(null);
  const [phoneDropdownOpen, setPhoneDropdownOpen] = useState(false);
  const phoneDropdownRef = useRef(null);

  // Sri Lanka hierarchical locations
  const [lkLocations, setLkLocations] = useState([]); // API root array
  const [lkLoading, setLkLoading] = useState(false);
  const [lkError, setLkError] = useState(null);
  const [province, setProvince] = useState("");
  const [district, setDistrict] = useState("");
  const [divisionalSecretariat, setDivisionalSecretariat] = useState("");
  const [gnd, setGnd] = useState("");
  const [village, setVillage] = useState("");

  // If onboarding already completed, redirect away immediately (no flicker)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.email) return;
      try {
        const res = await fetch(`${API_BASE_URL}/profile/${user.email}`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        const isCompleted = !!data.onboarding_completed || (typeof data.onboarding_step === 'number' && data.onboarding_step >= 4);
        if (!cancelled && isCompleted) {
          const dest = (user?.role === 'e2e-admin') ? '/admin-dashboard' : '/profile';
          navigate(`${dest}?onboarded=1`, { replace: true });
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [user, navigate]);

  useEffect(() => {
    if (!user?.email) return;
    setProfile((p) => ({ ...p, email: user.email }));
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/profile/${user.email}`);
        if (res.ok) {
          const data = await res.json();
          if (!data?.onboarding_completed) {
            setProfile((p) => ({
              ...p,
              firstName: data.firstName || "",
              lastName: data.lastName || "",
              location: data.location || "",
              phoneCode: data.phoneCode || "+94",
              phoneNumber: data.phoneNumber || "",
              email: data.email || user.email,
              industry: data.industry || "",
              department: data.department || "",
              position: data.position || "",
              products: Array.isArray(data.products) ? data.products : [], // legacy names (compat)
              machines: Array.isArray(data.machines) ? data.machines : [], // legacy names (compat)
            }));
            // Preload LK location hierarchy if present
            setProvince(data.province || "");
            setDistrict(data.district || "");
            setDivisionalSecretariat(data.divisional_secretariat || "");
            setGnd(data.grama_niladhari_division || "");
            setVillage(data.village || "");
            setStep(data.onboarding_step || 0);

            // Optionally initialize first entry from existing relational data
            if (Array.isArray(data.product_ids) && data.product_ids.length > 0) {
              setEntries([{ productId: Number(data.product_ids[0]), machineOptions: [], machineExperience: {} }]);
            }
          }
        }
      } catch (e) {}
    })();
  }, [user, navigate]);

  // Load products (once)
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${API_BASE_URL}/lookup/products`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (r.ok) setProductOptions(await r.json());
      } catch {}
    })();
  }, []);

  // Load country calling codes from external API (once)
  useEffect(() => {
    let cancelled = false;
    const normalizeDial = (s) => {
      if (!s || typeof s !== 'string') return null;
      const match = s.match(/\+[\d-]+/g);
      if (!match || !match[0]) return null;
      // Keep only digits, prefix with +
      const digits = match[0].replace(/[^\d]/g, '');
      if (!digits) return null;
      return `+${digits}`;
    };
    (async () => {
      try {
        setCountriesLoading(true);
        setCountriesError(null);
        const resp = await fetch('https://general.apivcm.shop/countries', { cache: 'no-store' });
        if (!resp.ok) throw new Error('Failed to load countries');
        const json = await resp.json();
        const arr = Array.isArray(json?.data) ? json.data : [];
        const mapped = arr
          .map((c) => {
            const value = normalizeDial(c?.international_dialing);
            if (!value) return null;
            const flag = c?.flag || '';
            const country = c?.country || '';
            const cc = c?.country_code || '';
            const label = `${flag ? flag + ' ' : ''}${value} ${country}`.trim();
            return { value, label, id: c?.id, cc, country };
          })
          .filter(Boolean)
          // Deduplicate on value (some territories share codes)
          .reduce((acc, cur) => {
            if (!acc.some((x) => x.value === cur.value)) acc.push(cur);
            return acc;
          }, [])
          .sort((a, b) => a.value.localeCompare(b.value));
        if (!cancelled) setCountryOptions(mapped);
      } catch (e) {
        if (!cancelled) setCountriesError(e?.message || 'Failed to load');
      } finally {
        if (!cancelled) setCountriesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // After countries load, ensure selected phoneCode is valid
  useEffect(() => {
    if (countriesLoading || countriesError || countryOptions.length === 0) return;
    const exists = countryOptions.some(o => o.value === profile.phoneCode);
    if (!exists) {
      const lk = countryOptions.find(o => o.value === '+94');
      setProfile(p => ({ ...p, phoneCode: lk ? lk.value : countryOptions[0].value }));
    }
  }, [countriesLoading, countriesError, countryOptions]);

  // Close phone dropdown on outside click or ESC
  useEffect(() => {
    if (!phoneDropdownOpen) return;
    const onDocClick = (e) => {
      if (!phoneDropdownRef.current) return;
      if (!phoneDropdownRef.current.contains(e.target)) setPhoneDropdownOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setPhoneDropdownOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [phoneDropdownOpen]);

  // Load Sri Lanka hierarchical data once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLkLoading(true);
        setLkError(null);
        const r = await fetch('https://general.apivcm.shop/sri-lanka-locations', { cache: 'no-store' });
        if (!r.ok) throw new Error('Failed to load Sri Lanka locations');
        const j = await r.json();
        if (!cancelled) setLkLocations(Array.isArray(j?.data) ? j.data : []);
      } catch (e) {
        if (!cancelled) setLkError(e?.message || 'Failed to load');
      } finally {
        if (!cancelled) setLkLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const isSriLanka = useMemo(() => profile.phoneCode === '+94', [profile.phoneCode]);

  // When phone code changes, auto-fill location for non-LK with country name; reset LK selections when switching
  useEffect(() => {
    const sel = countryOptions.find(c => c.value === profile.phoneCode);
    if (!isSriLanka) {
      if (sel?.country) setProfile(p => ({ ...p, location: sel.country }));
      // clear LK selections
      setProvince(""); setDistrict(""); setDivisionalSecretariat(""); setGnd(""); setVillage("");
    } else {
      // switching to LK - clear freeform location
      setProfile(p => ({ ...p, location: '' }));
    }
  }, [profile.phoneCode, countryOptions, isSriLanka]);

  // Derive dropdown options based on selections
  const provinceOptions = useMemo(() => lkLocations.map(p => p.province), [lkLocations]);
  const districtOptions = useMemo(() => {
    const p = lkLocations.find(x => x.province === province);
    return p ? (p.districts || []).map(d => d.district) : [];
  }, [lkLocations, province]);
  const dsOptions = useMemo(() => {
    const p = lkLocations.find(x => x.province === province);
    const d = p ? (p.districts || []).find(x => x.district === district) : null;
    return d ? (d.divisional_secretariats || []).map(ds => ds.divisional_secretariat) : [];
  }, [lkLocations, province, district]);
  const gndOptions = useMemo(() => {
    const p = lkLocations.find(x => x.province === province);
    const d = p ? (p.districts || []).find(x => x.district === district) : null;
    const ds = d ? (d.divisional_secretariats || []).find(x => x.divisional_secretariat === divisionalSecretariat) : null;
    return ds ? (ds.grama_niladhari_divisions || []).map(g => g.grama_niladhari_division) : [];
  }, [lkLocations, province, district, divisionalSecretariat]);
  const villageOptions = useMemo(() => {
    const p = lkLocations.find(x => x.province === province);
    const d = p ? (p.districts || []).find(x => x.district === district) : null;
    const ds = d ? (d.divisional_secretariats || []).find(x => x.divisional_secretariat === divisionalSecretariat) : null;
    const g = ds ? (ds.grama_niladhari_divisions || []).find(x => x.grama_niladhari_division === gnd) : null;
    return g ? (g.villages || []).map(v => v.village_name) : [];
  }, [lkLocations, province, district, divisionalSecretariat, gnd]);

  // When LK selection changes, compose a human readable location string
  useEffect(() => {
    if (!isSriLanka) return;
    const parts = [village, gnd, divisionalSecretariat, district, province].filter(Boolean);
    setProfile(p => ({ ...p, location: parts.join(', ') }));
  }, [isSriLanka, province, district, divisionalSecretariat, gnd, village]);

  // Helper: load machines for a product for a single entry
  const loadMachinesForEntry = async (idx, productId) => {
    const token = localStorage.getItem('token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    try {
      const r = await fetch(`${API_BASE_URL}/lookup/machines?product_id=${encodeURIComponent(productId)}`, { headers });
      if (!r.ok) return;
      const arr = await r.json();
      setEntries(prev => prev.map((e, i) => i === idx ? { ...e, machineOptions: arr, machineExperience: {} } : e));
    } catch {}
  };

  const garmentPositions = useMemo(() => {
    return GARMENT.positionsByDepartment[profile.department] || [];
  }, [profile.department]);

  // Dynamic Departments/Designations from backend
  const [deptOptions, setDeptOptions] = useState([]); // {id, department_code, name}
  const [desgOptions, setDesgOptions] = useState([]); // {id, designation_code, name}
  // Whether user has product/machine experience
  const [hasExperience, setHasExperience] = useState(true);
  useEffect(() => {
    if (profile.industry !== 'Garment Sector') return;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${API_BASE_URL}/lookup/departments`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (r.ok) setDeptOptions(await r.json());
      } catch {}
    })();
  }, [profile.industry]);
  useEffect(() => {
    if (!profile.department) { setDesgOptions([]); return; }
    // Find selected department id by name; if names diverge, could store id in state. For now, match by name.
    const dept = deptOptions.find(d => d.name === profile.department);
    if (!dept) { setDesgOptions([]); return; }
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${API_BASE_URL}/lookup/designations?department_id=${dept.id}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (r.ok) setDesgOptions(await r.json());
      } catch {}
    })();
  }, [profile.department, deptOptions]);

  const canNext = useMemo(() => {
    if (step === 0) {
      if (!profile.firstName.trim() || !profile.lastName.trim() || !profile.phoneNumber.trim()) return false;
      if (isSriLanka) {
        return !!(province && district && divisionalSecretariat && gnd && village);
      }
      return profile.location.trim().length > 0;
    }
    // For admins, industry selection is not required
    if (step === 1) return (user?.role === 'e2e-admin') ? true : !!profile.industry;
    if (step === 2 && profile.industry === "Garment Sector") {
      return !!profile.department && !!profile.position;
    }
    if (step === 3 && profile.industry === "Garment Sector") {
      if (!hasExperience) return true;
      // At least one entry must have a product and at least one machine with experience
      return entries.some(e => e.productId && Object.keys(e.machineExperience || {}).length > 0);
    }
    return true;
  }, [step, profile, entries, hasExperience]);

  const handleProfilePictureUpload = async (file) => {
    if (!file || !user?.email) return;
    const formData = new FormData();
    formData.append("profile_picture", file);
    await fetch(`${API_BASE_URL}/profile/${user.email}/picture`, { method: "POST", body: formData });
  };

  const saveProgress = async (complete = false) => {
    if (!user?.email) return;
    setSaving(true);
    try {
      // Build relational payload from entries
      const productIds = entries.map(e => e.productId).filter(Boolean);
      const machineExp = entries.flatMap(e => Object.entries(e.machineExperience || {}).map(([machine_id, experience_level]) => ({ machine_id: Number(machine_id), experience_level })));

      const payload = {
        ...profile,
        machines: profile.machines, // legacy (names)
        products: profile.products, // legacy (names)
        onboarding_step: complete ? 4 : step,
        onboarding_completed: complete ? 1 : 0,
        // new relational fields preferred by backend
        product_ids: hasExperience ? productIds : [],
        machine_experience: hasExperience ? machineExp : [],
        // new LK location hierarchy fields
        province: isSriLanka ? province : null,
        district: isSriLanka ? district : null,
        divisional_secretariat: isSriLanka ? divisionalSecretariat : null,
        grama_niladhari_division: isSriLanka ? gnd : null,
        village: isSriLanka ? village : null,
        location_country: !isSriLanka ? (countryOptions.find(c=>c.value===profile.phoneCode)?.country || profile.location || null) : 'Sri Lanka'
      };

      const formData = new FormData();
      formData.append("profileData", JSON.stringify(payload));

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/profile/${user.email}`, {
        method: "PUT",
        headers: token ? { 'Authorization': `Bearer ${token}` } : undefined,
        body: formData,
      });

      if (!res.ok) throw new Error("Failed to save");
      if (profilePicture) await handleProfilePictureUpload(profilePicture);

      if (complete) {
        if (user?.email) sessionStorage.setItem(`onboarded:${user.email}`, String(Date.now()));
        const dest = (user?.role === 'e2e-admin') ? '/admin-dashboard' : '/profile';
        navigate(`${dest}?onboarded=1`, { replace: true });
      } else setStep((s) => s + 1);
    } catch (e) {
      alert("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const MultiSelect = ({ options, value, onChange }) => (
    <div className="flex flex-wrap gap-3">
      {options.map((o) => (
        <button
          key={o}
          type="button"
          className={`px-4 py-2 rounded-full border text-base font-medium transition-colors duration-300 ${
            value.includes(o) ? "bg-indigo-600 text-white border-indigo-600" : "bg-white border-gray-300 hover:bg-indigo-50"
          }`}
          onClick={() => onChange(value.includes(o) ? value.filter((v) => v !== o) : [...value, o])}
        >
          {o}
        </button>
      ))}
    </div>
  );

  const stepVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 },
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-indigo-50 to-white">
      <Header />
      <main className="flex-1 w-full max-w-5xl mx-auto p-6 md:p-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 space-y-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Welcome! Let’s set up your profile</h1>
            <p className="text-gray-500 text-lg">Step {step + 1} of {profile.industry === "Garment Sector" ? 4 : 2}</p>
          </div>

          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.section
                key="step0"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={stepVariants}
                transition={{ duration: 0.5 }}
                className="space-y-6"
              >
                <h2 className="text-2xl font-semibold">Basic Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                  <input
                    className="border rounded-lg p-4 text-lg"
                    placeholder="First Name"
                    value={profile.firstName}
                    onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                  />
                  <input
                    className="border rounded-lg p-4 text-lg"
                    placeholder="Last Name"
                    value={profile.lastName}
                    onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                  />
                                    <div className="flex gap-4 relative">
                    {/* Custom phone code dropdown */}
                    <div className="w-40" ref={phoneDropdownRef}>
                      <button
                        type="button"
                        className="border rounded-lg p-4 text-lg w-full text-left bg-white"
                        onClick={() => setPhoneDropdownOpen((v) => !v)}
                      >
                        {countriesLoading ? 'Loading…' : profile.phoneCode}
                      </button>
                      {phoneDropdownOpen && (
                        <div className="absolute z-20 mt-1 w-[28rem] max-w-[90vw] bg-white border rounded-lg shadow-lg max-h-72 overflow-auto">
                          <div className="sticky top-0 bg-white border-b px-3 py-2 text-xs text-gray-500">
                            <div className="grid grid-cols-[64px_96px_1fr] gap-2">
                              <span className="uppercase">CC</span>
                              <span>Code</span>
                              <span>Country</span>
                            </div>
                          </div>
                          {countriesError && (
                            <ul>
                              {['+94','+91','+971'].map((code) => (
                                <li key={code}>
                                  <button
                                    type="button"
                                    className="w-full px-3 py-2 hover:bg-indigo-50 text-left"
                                    onClick={() => { setProfile({ ...profile, phoneCode: code }); setPhoneDropdownOpen(false); }}
                                  >
                                    <div className="grid grid-cols-[64px_96px_1fr] gap-2">
                                      <span className="text-gray-500 font-mono">--</span>
                                      <span className="font-medium font-mono">{code}</span>
                                      <span className="text-gray-700">&nbsp;</span>
                                    </div>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                          {!countriesError && (
                            <ul>
                              {countryOptions.map((o) => (
                                <li key={`${o.cc}-${o.value}`}>
                                  <button
                                    type="button"
                                    className="w-full px-3 py-2 hover:bg-indigo-50 text-left"
                                    onClick={() => { setProfile({ ...profile, phoneCode: o.value }); setPhoneDropdownOpen(false); }}
                                  >
                                    <div className="grid grid-cols-[64px_96px_1fr] gap-2">
                                      <span className="uppercase text-gray-500 font-mono">{o.cc || '--'}</span>
                                      <span className="font-medium font-mono">{o.value}</span>
                                      <span className="text-gray-700 truncate">{o.country}</span>
                                    </div>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                    <input
                      className="border rounded-lg p-4 text-lg flex-1"
                      placeholder="Mobile Number"
                      value={profile.phoneNumber}
                      onChange={(e) => setProfile({ ...profile, phoneNumber: e.target.value })}
                    />
                  </div>
                  {isSriLanka ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:col-span-2">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Province</label>
                        <select className="border rounded-lg p-3 w-full" value={province} onChange={(e)=>{ setProvince(e.target.value); setDistrict(''); setDivisionalSecretariat(''); setGnd(''); setVillage(''); }}>
                          <option value="">Select province</option>
                          {provinceOptions.map(p => (<option key={p} value={p}>{p}</option>))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">District</label>
                        <select className="border rounded-lg p-3 w-full" value={district} onChange={(e)=>{ setDistrict(e.target.value); setDivisionalSecretariat(''); setGnd(''); setVillage(''); }} disabled={!province}>
                          <option value="">Select district</option>
                          {districtOptions.map(d => (<option key={d} value={d}>{d}</option>))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Divisional Secretariat</label>
                        <select className="border rounded-lg p-3 w-full" value={divisionalSecretariat} onChange={(e)=>{ setDivisionalSecretariat(e.target.value); setGnd(''); setVillage(''); }} disabled={!district}>
                          <option value="">Select divisional secretariat</option>
                          {dsOptions.map(ds => (<option key={ds} value={ds}>{ds}</option>))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Grama Niladhari Division</label>
                        <select className="border rounded-lg p-3 w-full" value={gnd} onChange={(e)=>{ setGnd(e.target.value); setVillage(''); }} disabled={!divisionalSecretariat}>
                          <option value="">Select GND</option>
                          {gndOptions.map(g => (<option key={g} value={g}>{g}</option>))}
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm text-gray-700 mb-1">Village</label>
                        <select className="border rounded-lg p-3 w-full" value={village} onChange={(e)=> setVillage(e.target.value)} disabled={!gnd}>
                          <option value="">Select village</option>
                          {villageOptions.map(v => (<option key={v} value={v}>{v}</option>))}
                        </select>
                      </div>
                    </div>
                  ) : (
                    <input
                      className="border rounded-lg p-4 text-lg md:col-span-2"
                      placeholder="Location (City, Country)"
                      value={profile.location}
                      onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                    />
                  )}

                  <div className="flex flex-col items-center gap-4 md:col-span-2">
                    {profilePicture ? (
                      <div className="relative">
                        <img 
                          src={URL.createObjectURL(profilePicture)} 
                          alt="Profile Preview" 
                          className="w-24 h-24 rounded-full object-cover border-2 border-indigo-100"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setProfilePicture(null);
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                        <User size={32} />
                      </div>
                    )}
                    <label className="border rounded-lg p-4 flex items-center justify-between cursor-pointer w-full">
                      <span className="text-gray-600 text-lg">
                        {profilePicture ? 'Change Picture' : 'Upload Profile Picture'}
                      </span>
                      <div className="flex items-center gap-2">
                        <Upload size={22} />
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setProfilePicture(file);
                            }
                          }}
                        />
                      </div>
                    </label>
                  </div>
                </div>
              </motion.section>
            )}

            {step === 1 && user?.role !== 'e2e-admin' && (
              <motion.section
                key="step1"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={stepVariants}
                transition={{ duration: 0.5 }}
                className="space-y-6"
              >
                <h2 className="text-2xl font-semibold">Select your industry</h2>
                <select
                  className="border rounded-lg p-4 text-lg w-full"
                  value={profile.industry}
                  onChange={(e) => setProfile({ ...profile, industry: e.target.value })}
                >
                  <option value="">Choose an industry</option>
                  {INDUSTRIES.map((i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              </motion.section>
            )}

            {profile.industry === "Garment Sector" && step === 2 && (
              <motion.section
                key="step2"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={stepVariants}
                transition={{ duration: 0.5 }}
                className="space-y-6"
              >
                <h2 className="text-2xl font-semibold">Garment Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-lg text-gray-700">Department</label>
                    <select
                      className="border rounded-lg p-4 text-lg w-full mt-2"
                      value={profile.department}
                      onChange={(e) => setProfile({ ...profile, department: e.target.value, position: "" })}
                    >
                      <option value="">Select</option>
                      {deptOptions.map((d) => (
                        <option key={d.id} value={d.name}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-lg text-gray-700">Position</label>
                    <select
                      className="border rounded-lg p-4 text-lg w-full mt-2"
                      value={profile.position}
                      onChange={(e) => setProfile({ ...profile, position: e.target.value })}
                    >
                      <option value="">Select</option>
                      {desgOptions.map((p) => (
                        <option key={p.id} value={p.name}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </motion.section>
            )}

            {profile.industry === "Garment Sector" && step === 3 && (
              <motion.section
                key="step3"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={stepVariants}
                transition={{ duration: 0.5 }}
                className="space-y-6"
              >
                <h2 className="text-2xl font-semibold">Products & Machines</h2>
                <div className="space-y-6">
                  <div className="flex items-center gap-6">
                    <span className="text-lg text-gray-700">Have experience?</span>
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="hasExperience"
                        checked={hasExperience === true}
                        onChange={()=> setHasExperience(true)}
                      />
                      <span>Yes</span>
                    </label>
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="hasExperience"
                        checked={hasExperience === false}
                        onChange={()=> setHasExperience(false)}
                      />
                      <span>No</span>
                    </label>
                  </div>
                  {!hasExperience && (
                    <div className="text-sm text-gray-600 bg-gray-50 border rounded p-3">You can finish onboarding without adding product or machine experience.</div>
                  )}
                  {hasExperience && (
                    <>
                  {entries.map((entry, idx) => {
                    const chosenProductIds = new Set(entries.map((e, i) => i===idx ? null : e.productId).filter(Boolean));
                    const availableProducts = productOptions.filter(p => !chosenProductIds.has(p.id));
                    return (
                      <div key={idx} className="grid grid-cols-1 md:grid-cols-2 gap-6 border rounded-xl p-4">
                        <div>
                          <label className="text-lg text-gray-700">Product {idx+1}</label>
                          <select
                            className="border rounded-lg p-4 text-lg w-full mt-2"
                            value={entry.productId || ''}
                            onChange={(e)=>{
                              const pid = e.target.value ? Number(e.target.value) : null;
                              setEntries(prev => prev.map((en,i)=> i===idx ? { ...en, productId: pid, machineExperience: {}, machineOptions: [] } : en));
                              if (pid) loadMachinesForEntry(idx, pid);
                            }}
                          >
                            <option value="">Select a product</option>
                            {availableProducts.map(p => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-lg text-gray-700">Machines (with experience)</label>
                          <div className="mt-2 space-y-3">
                            {(!entry.machineOptions || entry.machineOptions.length === 0) && (
                              <div className="text-sm text-gray-500">{entry.productId ? 'No machines mapped to this product yet.' : 'Select a product first.'}</div>
                            )}
                            {entry.machineOptions.map(m => (
                              <div key={m.id} className="flex items-center gap-3">
                                <input
                                  type="checkbox"
                                  checked={typeof entry.machineExperience[m.id] !== 'undefined'}
                                  onChange={(e)=>{
                                    setEntries(prev => prev.map((en,i)=>{
                                      if (i!==idx) return en;
                                      const me = { ...(en.machineExperience||{}) };
                                      if (e.target.checked) me[m.id] = me[m.id] || '3m'; else delete me[m.id];
                                      return { ...en, machineExperience: me };
                                    }));
                                  }}
                                />
                                <span className="flex-1">{m.name}</span>
                                {typeof entry.machineExperience[m.id] !== 'undefined' && (
                                  <div className="flex flex-col gap-1 w-full max-w-md">
                                    <div className="flex items-center gap-3">
                                      <input
                                        type="range"
                                        min={0}
                                        max={6}
                                        step={1}
                                        value={["3m","6m","1y","2y","3y","4y","5y+"].indexOf(entry.machineExperience[m.id]) !== -1 ? ["3m","6m","1y","2y","3y","4y","5y+"].indexOf(entry.machineExperience[m.id]) : 0}
                                        onChange={(e)=>{
                                          const labels = ["3m","6m","1y","2y","3y","4y","5y+"];
                                          const val = labels[Number(e.target.value)] || '3m';
                                          setEntries(prev => prev.map((en,i)=>{
                                            if (i!==idx) return en;
                                            return { ...en, machineExperience: { ...en.machineExperience, [m.id]: val } };
                                          }));
                                        }}
                                        className="flex-1 accent-blue-600"
                                      />
                                      <span className="text-xs text-gray-700 w-12 text-right">{entry.machineExperience[m.id]}</span>
                                    </div>
                                    <div className="flex justify-between text-[10px] text-gray-400 px-1">
                                      {['3m','6m','1y','2y','3y','4y','5y+'].map(l => (
                                        <span key={l}>{l}</span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                        {idx>0 && (
                          <div className="md:col-span-2">
                            <button type="button" className="text-red-600 text-sm underline" onClick={()=>setEntries(prev=>prev.filter((_,i)=>i!==idx))}>Remove this experience</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div>
                    <button
                      type="button"
                      className="px-4 py-2 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
                      disabled={!hasExperience}
                      onClick={()=>{
                        // Add a new entry with first available product not already chosen
                        const chosen = new Set(entries.map(e=>e.productId).filter(Boolean));
                        const next = productOptions.find(p=>!chosen.has(p.id));
                        const newEntry = { productId: next ? next.id : null, machineOptions: [], machineExperience: {} };
                        setEntries(prev=>[...prev, newEntry]);
                        if (next) loadMachinesForEntry(entries.length, next.id);
                      }}
                    >
                      + Add another product experience
                    </button>
                  </div>
                    </>
                  )}
                </div>
              </motion.section>
            )}
          </AnimatePresence>

          <div className="flex justify-between mt-8">
            <button
              className="px-6 py-3 rounded-lg border flex items-center gap-2 text-lg font-medium hover:bg-gray-100 transition"
              disabled={step === 0 || saving}
              onClick={() => setStep((s) => Math.max(0, s - 1))}
            >
              <ChevronLeft size={20} /> Back
            </button>

            {((profile.industry !== "Garment Sector" && step >= 1) || (profile.industry === "Garment Sector" && step >= 3)) ? (
              <button
                className="px-6 py-3 rounded-lg bg-indigo-600 text-white flex items-center gap-2 text-lg font-medium hover:bg-indigo-700 transition disabled:opacity-50"
                disabled={!canNext || saving}
                onClick={() => saveProgress(true)}
              >
                <CheckCircle2 size={20} /> Finish
              </button>
            ) : (
              <button
                className="px-6 py-3 rounded-lg bg-indigo-600 text-white flex items-center gap-2 text-lg font-medium hover:bg-indigo-700 transition disabled:opacity-50"
                disabled={!canNext || saving}
                onClick={() => saveProgress(false)}
              >
                Next <ChevronRight size={20} />
              </button>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
